import RBush from "rbush";
import GameConfig from "../core/data/GameConfig";
import { GameObject } from "../core/GameObject";
import PlayerData from "../PlayerData";
import { GameScene } from "../scenes/GameScene";
import Overlay from "../utils/Overlay";
import { ChunkSpawner } from "./ChunkSpawner";
import { Coin } from "./Coin";
import { Confetti } from "./Confetti";
import { DestroyOffscreen } from "./DestroyOffscreen";
import { GameState } from "./GameState";
import { Ground } from "./Ground";
import { LaunchState } from "./LaunchState";
import { CharacterUnlockData, MissionsState } from "./MissionsState";
import { MovingObject } from "./MovingObject";
import { ObjectPool } from "./ObjectPool";
import { Player } from "./Player";
import { BGLayer, SetDecoLayer } from "./ScrollingBG";
import { Shadow } from "./Shadow";
import { Slingshot } from "./Slingshot";

export type MissionData = {
	type: string;
	goals: number[];
	index: number;
	rewards: number[];
}

type MissionOrder = MissionSlotData[][];

type MissionSlotData = {
	type: string;
	gatedBy?: string;
}

export type BoundingBox<T> = {
	minX: number;
	maxX: number;
	minY: number;
	maxY: number;
	obj: T;
}

export class LTGame {

	scene: GameScene;
	state: GameState;

	chunkSpawner: ChunkSpawner;

	coinRtree: RBush<BoundingBox<Coin>>;
	coinPool: ObjectPool<Coin>;
	movingObjectPools: ObjectPool<MovingObject>[] = [];

	player: Player;
	slingshot: Slingshot;
	inputObj: Phaser.GameObjects.Image;

	startX: number;
	currentEnvironment = 0;
	startTweens: Phaser.Tweens.Tween[] = [];
	trumpets: Phaser.GameObjects.Image[] = [];

	coins = 0;
	overlayObj: GameObject;

	constructor(scene: GameScene) {
		this.scene = scene;
		this.startX = this.scene.viewport.width/2 - 10;
		this.chunkSpawner = new ChunkSpawner(scene);

		this.spawnStartDeco();
		const quickStart = (GameConfig.get("quickStart") || !PlayerData.Instance.saveData.launchedSlingshot);
		const layer = quickStart ? "foreground" : "player";
		if (!PlayerData.Instance.saveData.seenSlamTut) {
			const overlayObj = this.overlayObj = new GameObject(scene);
			const overlay = Overlay.create(scene, 0.5);
			overlayObj.add(overlay);
			scene.addObject(overlayObj, layer);
			overlayObj.alpha = 0;
			overlayObj.scale = 3;
		}
		const slingshot = this.slingshot = scene.addObject(new Slingshot(scene), layer);
		const player = this.player = scene.addObject(new Player(scene), "player");
		player.setPosition(0, 0);
		player.on(Player.ON_COLLECT_COINS, (amt: number) => this.onCollectCoin(amt));
		player.on(Player.ON_UPDATE_SLAM_METER, (amt: number) => this.scene.ui.updateSlamMeter(amt));
		player.on(Player.ON_SLAM_FAILED, (amt: number) => this.scene.ui.slamFail());
		player.addSlam(0);

		const shadow = new Shadow(scene);
		scene.addObject(shadow, "defaultBack");
		shadow.player = player;

		this.scene.cameraController.follow(player);

		slingshot.setPosition(this.startX, 643);
		this.player.planckBody.setPosition(slingshot.x + 20, slingshot.y - 30);
		this.player.planckBody.setKinematic();
		slingshot.setPlayer(player);

		this.coinRtree = new RBush<BoundingBox<Coin>>();
		scene.events.on(GameScene.ON_OBJECT_ADDED, this.onObjectAdded, this);

		this.coinPool = new ObjectPool(() => new Coin(scene, 0, 0), 200);
		for (let i = 0; i < 4; i++) {
			this.movingObjectPools[i] = new ObjectPool(() => new MovingObject(scene, i), 25);
		}

		this.populateMissions();
		this.scene.ui.launchMeter.pause(true);
		if (quickStart) {
			this.switchState(new LaunchState(this, scene));
			this.scene.ui.launchMeter.setVisible(false);
			this.scene.ui.launchMeter.getPower = (): number => {
				return this.scene.ui.launchMeter.max;
			}
			const tweenDuration = GameConfig.get("slowTutTweenDuration") as number;
			const player = this.player;
			if (this.overlayObj) {
				this.overlayObj.setPosition(player.x, player.y);
				const tween = this.scene.tweens.add({
					targets: this.overlayObj,
					alpha: 1,
					duration: tweenDuration,
				});
			}
		}
		else this.switchState(new MissionsState(this, scene));
	}

	shutdown(): void {
		this.scene.events.off(GameScene.ON_OBJECT_ADDED);
		this.state?.shutdown();
	}

	onObjectAdded(obj: GameObject): void {
		// put all coins in an RTree for fast collision queries
		if (obj instanceof Coin) {
			const boundingBox = obj.getHitbox();
			this.coinRtree.insert(boundingBox);
			obj.once(Phaser.GameObjects.Events.DESTROY, () => {
				this.coinRtree.remove(boundingBox);
			});
		}
	}

	spawnStartDeco() {
		const fg = this.scene.scrollingBG.layers[4];
		const addImage = (layer: BGLayer, key: string, x: number, y: number) => {
			const img = layer.addImage(key);
			img.setOrigin(0, 1);
			img.setPosition(x, y - this.chunkSpawner.groundHeight);
			return img;
		}
		const oy = 0;
		addImage(fg, "backlotWBTower", 1780, -420 + oy);

		for (let x = 0; x < 6; x++) {
			for (let y = 0; y < 4; y++) {
				addImage(fg, "backlotWall", 286 * x - 164, -216 - (286 * y));
			}
			addImage(fg, "backlotTop", 256 * x + 50 - 164, -1308);
		}
		const rays = addImage(fg, "backlotFrontWallLightRays", 0, 0);
		const frontWall = addImage(fg, "backlotFrontWall", 1524, -216);
		const glow = addImage(fg, "backlotFrontWallLightGlow", 0, 0);
		const t1 = this.scene.tweens.create({
			targets: [rays,glow],
			alpha: 0.5, 
			duration: 1000,
			yoyo: true,
			loop: -1,
			ease: Phaser.Math.Easing.Quadratic.InOut,
		});
		this.startTweens.push(t1);

		glow.setOrigin(0.5, 0.5);
		rays.setOrigin(0.5, 0.5);
		glow.setPosition(frontWall.x + 360, frontWall.y - 312);
		rays.setPosition(glow.x, glow.y);
		const haze = addImage(fg, "backlotWallHaze", -380, -216);
		haze.displayWidth = 1940;
		haze.width = haze.displayWidth;
		let itemsLeft = addImage(fg, "backlotItemsLeft", 100, oy);
		let itemsLeftBottom = addImage(fg, "backlotItemsLeft", 0, 0);
		itemsLeftBottom.scaleY = -20;
		addImage(fg, "backlotItemsRight", itemsLeft.x + itemsLeft.width, oy);
		const itemsRightBottom = addImage(fg, "backlotItemsRight", itemsLeft.x + itemsLeft.width - 20, 0);
		itemsRightBottom.scaleY = -20;
		addImage(fg, "backlotFrontWallTop", frontWall.x - 105, (frontWall.y - frontWall.height) + 187);
		addImage(fg, "backlotBillboard2", 180, -700);
		addImage(fg, "backlotBillboard1", 780, -700);

		const blueHaze = addImage(fg, "backlotBlueHaze", 0, oy);
		blueHaze.displayWidth = 2600;

		const addObject = (layer: "default" | "bg" | "foreground", key: string, x: number, y: number) => {
			const img = new GameObject(this.scene);
			const actualImg = img.addImage(key);
			img.setPosition(x, y);
			this.scene.addObject(img, layer);
			img.addComponent(new DestroyOffscreen(img, true, true, 2000));
			return actualImg;
		}

		addObject("default", "backlotStageTop", 230 + this.startX, 507);		
		const lightBucketL = addObject("default", "backlotStageLightBucket", this.startX - 280, 800);
		const lightBucketR = addObject("default", "backlotStageLightBucket", 735 + this.startX, 800);
		const lightBaseL = addObject("default", "backlotStageLightBase", this.startX - 280, 830);
		const lightBaseR = addObject("default", "backlotStageLightBase", 735 + this.startX, 830);
		lightBucketL.setOrigin(0.91, 0.9);
		lightBucketR.setFlipX(true);
		lightBucketR.setOrigin(0.09, 0.9);
		lightBaseR.setFlipX(true);
		const t2 = this.scene.tweens.create({
			targets: lightBucketL,
			angle: lightBucketL.angle - 25,
			duration: 1000,
			yoyo: true,
			loop: -1,
			ease: Phaser.Math.Easing.Quadratic.InOut,
		});
		const t3 = this.scene.tweens.create({
			targets: lightBucketR,
			angle: lightBucketR.angle + 25,
			duration: 1000,
			yoyo: true,
			loop: -1,
			ease: Phaser.Math.Easing.Quadratic.InOut,
		});
		this.startTweens.push(t2, t3);
		addObject("default", "backlotStageBottom", 230 + this.startX, 847);

		for (let i = 0; i < 3; i++) {
			const floor = new Ground(this.scene, i * 1017, this.scene.viewport.height - this.chunkSpawner.groundHeight / 2, "backlotFloor")
			this.scene.layers.get("midBG").add(floor);
		}

		const startHill = (<Phaser.GameObjects.Image[]>fg.list).find(o => o.texture.key == "bedrockFGStart");
		fg.bringToTop(startHill);
		const floor = new Ground(this.scene, 2500, this.scene.viewport.height - this.chunkSpawner.groundHeight / 2, "backlotFloorTransition");
		this.scene.addObject(floor, "midBG");

		const trumpetsLeft = addObject("foreground", "backlotTrumpets", 245, 810);
		const trumpetsRight = addObject("foreground", "backlotTrumpets", 240 + this.scene.viewport.width, 810);
		this.trumpets = [trumpetsLeft, trumpetsRight];
		trumpetsRight.scaleX = -1;

		trumpetsLeft.x -= trumpetsLeft.width;
		trumpetsLeft.y += trumpetsLeft.height;
		trumpetsRight.x += trumpetsRight.width;
		trumpetsRight.y += trumpetsRight.height;

		const t4 = this.scene.tweens.create({
			targets: trumpetsLeft,
			x: trumpetsLeft.x + trumpetsLeft.width,
			y: trumpetsLeft.y - trumpetsLeft.height,
			duration: 750,
			ease: Phaser.Math.Easing.Back.Out,
		});

		const t5 = this.scene.tweens.create({
			targets: trumpetsRight,
			x: trumpetsRight.x - trumpetsRight.width,
			y: trumpetsRight.y - trumpetsRight.height,
			duration: 750,
			ease: Phaser.Math.Easing.Back.Out,
		});
		this.startTweens.push(t4, t5);
		const sign = addObject("foreground", "backlotDangerSign", this.scene.viewport.width - 100, 1220);
		sign.setOrigin(0.75, 1);
		sign.angle = 90;

		const t6 = this.scene.tweens.create({
			targets: sign,
			angle: 0,
			duration: 750,
			ease: Phaser.Math.Easing.Back.Out,
		});
		this.startTweens.push(t6);

		const decoLayer = this.scene.scrollingBG.layers.find(l => l instanceof SetDecoLayer);
		const str = "billboardBedrock";
		const billboard = decoLayer.addImage(str);
		billboard.setOrigin(0, 1);
		billboard.setPosition(1920 * 3, decoLayer.imgYOffset - 100);
	}

	populateMissions(): void {
		const saveData = PlayerData.Instance.saveData;
		const missions = saveData.missions;
		const missionDatas = GameConfig.get("missions") as MissionData[];
		const missionOrders = GameConfig.get("missionOrder") as MissionOrder;
		for (let i = 0; i < 3; i++) {
			const mission = missions[i];

			if (mission == null) {
				// get the next mission type for this slot, in the order defined in missionOrder
				const missionOrder = missionOrders[i];
				let validType = true;
				let missionType: MissionSlotData;
				do {
					validType = true;
					// increment mission type for this slot
					let missionTypeIndex = (saveData.lastMissionIndex[i] + 1) % missionOrders[i].length;
					missionType = missionOrder[missionTypeIndex];
					saveData.lastMissionIndex[i] = missionTypeIndex;

					// if mission type is gated, skip it unless player's completed one mission from its gated type.
					// (e.g. don't show "break walls" missions until after "reach environment" missions for some reason)
					if (missionType.gatedBy) {
						const gateMissionsComplete = saveData.completedMissions[missionType.gatedBy];
						if (gateMissionsComplete == null || gateMissionsComplete == 0) {
							validType = false;
						}
					}
				} while (validType == false)

				// get the mission data for the selected mission type
				const missionData = missionDatas.find(m => m.type == missionType.type);
				// choose a mission based on how many missions of this type the player has already completed
				let progressIndex = saveData.completedMissions[missionData.type];
				if (progressIndex == null) {
					progressIndex = 0;
					saveData.completedMissions[missionData.type] = 0;
				}
				if (progressIndex >= missionData.goals.length) {
					progressIndex = missionData.goals.length - 1;
				}

				missions[i] = {
					type: missionData.type,
					goal: missionData.goals[progressIndex],
					progress: 0,
					index: progressIndex,
					reward: missionData.rewards[progressIndex],
					completed: false,
				};
			}
		}

		const characterUnlocks = GameConfig.get<CharacterUnlockData[]>("characterUnlocks");
		if (saveData.missionsToNextCharacterUnlock == 0) {
			saveData.missionsToNextCharacterUnlock = characterUnlocks[saveData.charactersUnlocked + 1].missionsToComplete;
		}

		PlayerData.Instance.save();
	}

	switchState(state: GameState): void {
		if (this.state) this.state.shutdown();
		this.state = state;
		this.state.start();
	}

	update(dt: number): void {
		this.state.update(dt);
		this.chunkSpawner.update();
	}

	onInput(): void {
		console.log("input");
	}

	onCollectCoin(amt: number) {
		this.coins += amt;
		this.scene.ui.updateCoins(this.coins);
	}

	onPassedWallMidpoint(): void {
		this.state?.onPassedWallMidpoint();
	}

	getPlayerDistance() {
		const pixelsPerMeter = 100;
		return Math.floor((this.player.x - this.startX) / pixelsPerMeter);
	}

	playIntroTweens(): void {
		for (const tween of this.startTweens) tween.play();
		this.scene.ui.launchMeter.playIntroTween();
	}

	playExitTweens(): void {
		if (!this.trumpets[0]) return;
		const container = this.scene.add.container(this.startX, -this.scene.viewport.height/2);
		const fg = this.trumpets[0].parentContainer;
		fg.add(container);
		container.setSize(this.scene.viewport.width, this.scene.viewport.height);
		this.scene.addResize(() => {
			this.scene.alignIn.Center(container, this.scene.viewport);
		});
		const emitters = Confetti.createBurst(this.scene, container);
		this.scene.time.addEvent({
			delay: 100,
			callback: () => {
				Confetti.pauseEmitters(emitters);
			}
		});
		this.scene.tweens.add({
			targets: this.trumpets[0],
			x: this.trumpets[0].x - this.trumpets[0].width,
			y: this.trumpets[0].y + this.trumpets[0].height,
			duration: 500,
			ease: Phaser.Math.Easing.Quadratic.In,
		});

		this.scene.tweens.add({
			targets: this.trumpets[1],
			x: this.trumpets[1].x + this.trumpets[1].width,
			y: this.trumpets[1].y + this.trumpets[1].height,
			duration: 500,
			ease: Phaser.Math.Easing.Quadratic.In,
		});
	}
}